import { Link, router } from '@inertiajs/react'
import { AdminSidebar } from '@/components/layout/AdminSidebar'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

interface UserDetail {
  id: number
  name: string
  email: string
  subscription_status: string
  admin: boolean
  github_connected: boolean
  created_at: string
}

interface Props {
  user: UserDetail
}

export default function Show({ user }: Props) {
  const isPro = user.subscription_status === 'pro'
  const isCancelled = user.subscription_status === 'cancelled'

  const grantPro = () => router.patch(`/admin/users/${user.id}`, { grant_pro: true })
  const revokePro = () => {
    if (confirm('Revoke Pro access for this user?')) {
      router.patch(`/admin/users/${user.id}`, { revoke_pro: true })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar active="/admin/users" />
      <main className="flex-1 p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link href="/admin/users" className="text-sm text-gray-400 hover:text-gray-600">
            ← Users
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">{user.name}</h1>
          {user.admin && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
              Admin
            </span>
          )}
          <span className={cn(
            'text-xs font-semibold px-2 py-0.5 rounded-full capitalize ml-auto',
            isPro ? 'bg-brand/10 text-brand' :
            isCancelled ? 'bg-red-100 text-red-600' :
            'bg-gray-100 text-gray-500'
          )}>
            {user.subscription_status}
          </span>
        </div>

        <div className="max-w-xl space-y-5">
          {/* Profile card */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Profile</h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Email</dt>
                <dd className="font-medium text-gray-900">{user.email}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Name</dt>
                <dd className="font-medium text-gray-900">{user.name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">GitHub</dt>
                <dd className="font-medium text-gray-900">
                  {user.github_connected ? '✓ Connected' : '— Not connected'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Joined</dt>
                <dd className="font-medium text-gray-900">
                  {new Date(user.created_at).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>

          {/* Subscription management */}
          {!user.admin && (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Subscription</h2>
              <p className="text-sm text-gray-500 mb-5">
                Current status:{' '}
                <span className={cn(
                  'font-semibold capitalize',
                  isPro ? 'text-brand' : isCancelled ? 'text-red-500' : 'text-gray-700'
                )}>
                  {user.subscription_status}
                </span>
              </p>
              <div className="flex gap-3">
                {isPro ? (
                  <Button variant="secondary" onClick={revokePro}>
                    Revoke Pro
                  </Button>
                ) : (
                  <Button onClick={grantPro}>
                    Grant Pro
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
